import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { PiCameraBold, PiTrashBold } from 'react-icons/pi';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import { cn } from '@v2/utils/cn';

type ColorPhoto = {
    color_name: string;
    hex: string | null;
    image: { id: number; url: string | null } | null;
};

type Payload = { can_edit: boolean; colors: ColorPhoto[] };

/** Foto do equipamento por cor de estrutura — usada no deal, na PO e no configurador. */
export default function ProductColorPhotos({ productId }: { productId: number }) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const queryKey = ['product-color-images', productId];
    const fileRef = useRef<HTMLInputElement>(null);
    const [target, setTarget] = useState<string | null>(null);

    const { data } = useQuery({
        queryKey,
        queryFn: async () => (await api.get<{ data: Payload }>(`/products/${productId}/color-images`)).data.data,
    });

    const upload = useMutation({
        mutationFn: async ({ colorName, file }: { colorName: string; file: File }) => {
            const form = new FormData();
            form.append('color_name', colorName);
            form.append('image', file);
            return (await api.post<{ data: Payload }>(`/products/${productId}/color-images`, form)).data.data;
        },
        onSuccess: (payload) => {
            qc.setQueryData(queryKey, payload);
            toast.success(t('product.color_photo_saved'));
        },
        onError: (err: { response?: { data?: { message?: string } } }) =>
            toast.error(err?.response?.data?.message ?? t('product.color_photo_error')),
    });

    const remove = useMutation({
        mutationFn: async (imageId: number) =>
            (await api.delete<{ data: Payload }>(`/products/${productId}/color-images/${imageId}`)).data.data,
        onSuccess: (payload) => qc.setQueryData(queryKey, payload),
    });

    if (!data || data.colors.length === 0) return null;

    const pick = (colorName: string) => {
        setTarget(colorName);
        fileRef.current?.click();
    };

    return (
        <div>
            <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && target) upload.mutate({ colorName: target, file });
                    e.target.value = '';
                }}
            />
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{t('product.color_photos_hint')}</p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {data.colors.map((c) => {
                    const busy = upload.isPending && target === c.color_name;
                    return (
                        <div
                            key={c.color_name}
                            className="group overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800"
                        >
                            <button
                                type="button"
                                disabled={!data.can_edit || busy}
                                onClick={() => pick(c.color_name)}
                                className={cn(
                                    'relative flex aspect-[4/3] w-full items-center justify-center bg-gray-50 dark:bg-gray-900',
                                    data.can_edit && 'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800',
                                )}
                                title={data.can_edit ? t('product.color_photo_upload') : undefined}
                            >
                                {c.image?.url ? (
                                    <img src={c.image.url} alt={c.color_name} loading="lazy" className="h-full w-full object-contain p-2" />
                                ) : (
                                    <span className="flex flex-col items-center gap-1 text-[11px] text-gray-400">
                                        <PiCameraBold className="size-6" />
                                        {data.can_edit ? t('product.color_photo_upload') : t('product.color_photo_missing')}
                                    </span>
                                )}
                                {busy && <span className="absolute inset-0 animate-pulse bg-white/60 dark:bg-gray-900/60" />}
                            </button>
                            <div className="flex items-center gap-2 border-t border-gray-100 px-3 py-2 dark:border-gray-700">
                                <span
                                    className="size-4 shrink-0 rounded-full ring-1 ring-black/10"
                                    style={{ backgroundColor: c.hex ?? '#e5e7eb' }}
                                />
                                <span className="min-w-0 flex-1 truncate text-xs font-medium text-gray-800 dark:text-gray-100">
                                    {c.color_name}
                                </span>
                                {data.can_edit && c.image && (
                                    <button
                                        type="button"
                                        onClick={() => remove.mutate(c.image!.id)}
                                        className="text-gray-400 hover:text-rose-600"
                                        aria-label={t('product.color_photo_remove')}
                                        title={t('product.color_photo_remove')}
                                    >
                                        <PiTrashBold className="size-3.5" />
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
